import { useMemo } from "react";
import { Box, Text, VStack, HStack, Button, Spinner } from "@chakra-ui/react";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import {
  useGetMyDocumentsQuery,
  useLazyDownloadUserDocumentQuery,
  useUploadUserDocumentMutation,
  useDeleteUserDocumentMutation,
} from "../../../../redux/reducers/apiSlice";

const DOC_TYPE_LABELS = [
  "Strategic Plan",
  "990",
  "Annual Report",
  "Other Document",
];

const MAX_FILES = 5;

const TabDocuments = () => {
  const {
    data: documents,
    isLoading: isLoadingDocs,
    isFetching,
  } = useGetMyDocumentsQuery();
  const [uploadDocument, { isLoading: isUploading }] =
    useUploadUserDocumentMutation();
  const [deleteDocument, { isLoading: isDeleting }] =
    useDeleteUserDocumentMutation();
  const [downloadDocument] = useLazyDownloadUserDocumentQuery();

  const currentCount = documents?.length ?? 0;
  const remainingSlots = useMemo(
    () => Math.max(0, MAX_FILES - currentCount),
    [currentCount]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "image/*": [".png", ".jpg", ".jpeg", ".gif"],
    },
    maxFiles: remainingSlots,
    onDrop: async (acceptedFiles) => {
      if (remainingSlots <= 0) {
        toast.error(`You can upload up to ${MAX_FILES} documents.`);
        return;
      }
      const toUpload = acceptedFiles.slice(0, remainingSlots);
      if (!toUpload.length) {
        toast.error(`You can upload up to ${MAX_FILES} documents.`);
        return;
      }

      try {
        for (let i = 0; i < toUpload.length; i++) {
          const file = toUpload[i];
          const typeLabel =
            DOC_TYPE_LABELS[
              Math.min(currentCount + i, DOC_TYPE_LABELS.length - 1)
            ] || DOC_TYPE_LABELS[DOC_TYPE_LABELS.length - 1];

          const formData = new FormData();
          formData.append("file", file);
          formData.append("typeLabel", typeLabel);

          // Each upload invalidates the User tag so the list refreshes automatically
          // when useGetMyDocumentsQuery is mounted.
          await uploadDocument(formData).unwrap();
        }
        toast.success(
          `Uploaded ${toUpload.length} document${
            toUpload.length > 1 ? "s" : ""
          }.`
        );
      } catch (err: any) {
        console.error("UploadDocumentError", err);
        toast.error(
          err?.data?.message || "Failed to upload document(s). Please try again."
        );
      }
    },
  });

  const handleDelete = async (docId: string) => {
    try {
      await deleteDocument({ documentId: docId }).unwrap();
      toast.success("Document removed.");
    } catch (err: any) {
      console.error("DeleteDocumentError", err);
      toast.error(err?.data?.message || "Failed to delete document.");
    }
  };

  const handleDownload = async (doc: { id: string; fileName?: string; file_name?: string }) => {
    try {
      const blob = await downloadDocument(doc.id).unwrap();
      const name = doc.fileName || doc.file_name || "document";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error("DownloadDocumentError", err);
      toast.error("Failed to download document.");
    }
  };

  const isBusy = isUploading || isDeleting || isFetching;

  return (
    <Box>
      <Text mb={4} color="#4A5568" fontSize="sm">
        Upload up to {MAX_FILES} documents (Strategic Plan, 990, Annual Report,
        or other relevant documents) to make them available from your IGNITE
        account.
      </Text>

      <VStack align="stretch" spacing={4}>
        <Box
          {...getRootProps()}
          p={8}
          border="2px dashed"
          borderColor={isDragActive ? "orange.400" : "gray.300"}
          borderRadius="lg"
          textAlign="center"
          cursor={remainingSlots > 0 ? "pointer" : "not-allowed"}
          bg={isDragActive ? "gray.50" : "transparent"}
          _hover={
            remainingSlots > 0
              ? { borderColor: "orange.400", bg: "gray.50" }
              : {}
          }
        >
          <input {...getInputProps()} />
          <Text color="gray.700">
            Drag & drop files here, or click to select (PDF, Word, Excel,
            images)
          </Text>
          <Text fontSize="sm" color="gray.500" mt={2}>
            {currentCount}/{MAX_FILES} files uploaded
          </Text>
          {remainingSlots <= 0 && (
            <Text fontSize="xs" color="red.500" mt={1}>
              You have reached the maximum of {MAX_FILES} documents.
            </Text>
          )}
        </Box>

        {isLoadingDocs && (
          <HStack spacing={2} color="gray.600">
            <Spinner size="sm" />
            <Text fontSize="sm">Loading your documents…</Text>
          </HStack>
        )}

        {!isLoadingDocs && (documents?.length ?? 0) === 0 && (
          <Text fontSize="sm" color="gray.500">
            No documents uploaded yet.
          </Text>
        )}

        {!isLoadingDocs &&
          documents?.map((doc: any) => (
            <Box
              key={doc.id}
              p={3}
              bg="gray.50"
              borderRadius="md"
              fontSize="sm"
            >
              <Text fontWeight="medium">
                {doc.typeLabel || doc.type_label || "Document"}:
              </Text>
              <Text color="gray.700">{doc.fileName || doc.file_name}</Text>
              <HStack justify="space-between" mt={2}>
                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="orange"
                  onClick={() => handleDownload(doc)}
                >
                  Download
                </Button>
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => handleDelete(doc.id)}
                  isDisabled={isBusy}
                >
                  Remove
                </Button>
              </HStack>
            </Box>
          ))}
      </VStack>

      {isBusy && (
        <Text mt={3} fontSize="xs" color="gray.500">
          Updating your documents…
        </Text>
      )}
    </Box>
  );
};

export default TabDocuments;
